import React, { useEffect, useState } from 'react';

import Skeleton from '@mui/material/Skeleton';
import { qIllustrationLoaders, type QIllustrationName } from 'app-components/assets/QIllustration/qIllustrationLoaders';

import fallbackImage from 'assets/logos/Quicken-no-preview-light.svg';

const FALLBACK_SRC = fallbackImage;

const illustrationSizes = {
  xxs: 32,
  xs: 56,
  sm: 72,
  md: 96,
  lg: 128, // default
  xl: 168,
  xxl: 208,
} as const;

export type IllustrationSize = keyof typeof illustrationSizes;

export type QIllustrationProps = {
  /** Name of the illustration to render */
  name: QIllustrationName;
  /** Predefined size key (overridden by width/height if provided) */
  size?: IllustrationSize;
  /** Custom width in px (overrides size) */
  width?: number;
  /** Custom height in px (overrides size) */
  height?: number;
} & React.ImgHTMLAttributes<HTMLImageElement>;

function QIllustrationInternal({ name, size = 'lg', width, height, ...otherProps }: QIllustrationProps) {
  const [src, setSrc] = useState<string>();
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    setIsFallback(false);
    qIllustrationLoaders[name]()
      .then((module) => {
        setSrc(module.default);
      })
      .catch(() => {
        setSrc(FALLBACK_SRC);
        setIsFallback(true);
      });
  }, [name]);

  // When src changes, load the image to get its aspect ratio
  useEffect(() => {
    if (!src) return;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = src;
  }, [src]);

  let resolvedWidth = width;
  let resolvedHeight = height;

  if (aspectRatio) {
    if (width && !height) {
      resolvedHeight = Math.round(width / aspectRatio);
    } else if (!width && height) {
      resolvedWidth = Math.round(height * aspectRatio);
    }
  }

  // If neither width nor height is provided, use size
  if (!resolvedWidth && !resolvedHeight) {
    resolvedWidth = illustrationSizes[size];
    resolvedHeight = illustrationSizes[size];
  }

  const handleImgError = () => {
    if (!isFallback) {
      setSrc(FALLBACK_SRC);
      setIsFallback(true);
    }
  };

  return src && resolvedWidth && resolvedHeight ? (
    <img
      sharedcomponentid='QIllustration'
      src={src}
      width={resolvedWidth}
      height={resolvedHeight}
      loading='lazy'
      style={isFallback ? { filter: 'grayscale(1)' } : undefined}
      onError={handleImgError}
      {...otherProps}
    />
  ) : (
    <Skeleton
      variant='rectangular'
      width={resolvedWidth ?? illustrationSizes[size]}
      height={resolvedHeight ?? illustrationSizes[size]}
      sx={{ borderRadius: 2 }}
      {...otherProps}
    />
  );
}

export default QIllustrationInternal;
